import { formatTime } from './format';
import type { Best } from '../core/storage';
import { gridLabel, MAX_N, MIN_N, type Grid, type LevelMods } from '../core/levels';
import { modLabels, starsEl } from './stars';

export type CardModel = {
  placeId: string;
  title: string;
  /** 未完成显示 teaser，完成后显示 story */
  text: string;
  completed: boolean;
  /** 这一关的格数（关卡表决定，4..9）；长方形时是列数 */
  n: number;
  /** 这一关的格（城市 n×n；塔罗竖版 4×6 … 7×10） */
  grid?: Grid;
  /** 章节标题（塔罗），显示在标题上方 */
  chapter?: string | null;
  /** 这一关的玩法（旋转块 / 背面块），卡上显示小标签 */
  mods: LevelMods;
  /** 最好星级（0 = 还没有） */
  stars: number;
  best: Best | null;
  /** 本地点的中途存档 */
  resume: { n: number; grid?: Grid } | null;
  catFound: boolean;
  /** 符文（塔罗）是否找到；null / 缺省 = 这张图没有符文 */
  runeFound?: boolean | null;
  /** 加载中 / 失败状态 */
  status: 'idle' | 'loading' | 'error';
};

export type CardActions = {
  start: () => void;
  resume: () => void;
  view: () => void;
  close: () => void;
};

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

const icon = (src: string, cls = 'ui-icon') => {
  const i = el('img', cls);
  i.src = src;
  i.alt = '';
  i.draggable = false;
  return i;
};

/** 地点卡（spec §4.1）：DOM 叠层，底图是 card.png 九宫格；格数只展示，不可选 */
export class PlaceCard {
  private readonly layer: HTMLElement;
  model: CardModel | null = null;

  constructor(private readonly actions: CardActions) {
    this.layer = document.querySelector<HTMLElement>('#card-layer')!;
    // 点卡片外面 = 关闭
    this.layer.addEventListener('click', (e) => {
      if (e.target === this.layer && this.model?.status !== 'loading') this.actions.close();
    });
  }

  get visible(): boolean {
    return !this.layer.hidden;
  }

  show(m: CardModel): void {
    this.model = m;
    this.render();
    this.layer.hidden = false;
  }

  update(patch: Partial<CardModel>): void {
    if (!this.model) return;
    this.model = { ...this.model, ...patch };
    this.render();
  }

  hide(): void {
    this.layer.hidden = true;
    this.model = null;
  }

  private render(): void {
    const m = this.model!;
    const a = this.actions;
    const card = el('section', 'ui-card');
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', m.title);
    card.dataset.place = m.placeId;

    const close = el('button', 'ui-card-close');
    close.type = 'button';
    close.setAttribute('aria-label', '关闭');
    close.append(icon('/ui/icons/close.png'));
    close.onclick = () => m.status !== 'loading' && a.close();

    const title = el('h2', 'card-title', m.title);
    const chapter = m.chapter ? el('p', 'card-chapter', m.chapter) : null;
    const text = el('p', `card-text ${m.completed ? 'is-story' : 'is-teaser'}`, m.text);

    // 格数由关卡决定（core/levels），这里只展示，不让选
    const size = el('div', 'card-size');
    const g = m.grid ?? { cols: m.n, rows: m.n };
    size.dataset.n = String(m.n);
    const chip = el('span', 'ui-chip is-on card-size-chip', gridLabel(g));
    // 难度点按块数折算成 4..9 的"等效边长"（7×10 = 70 块 ≈ 8.4 → 8 个点）
    const eq = Math.round(Math.sqrt(g.cols * g.rows));
    const pips = el('span', 'card-size-pips');
    pips.setAttribute('aria-label', `难度 ${eq - MIN_N + 1}/${MAX_N - MIN_N + 1}`);
    for (let k = MIN_N; k <= MAX_N; k++) pips.append(el('i', k <= eq ? 'is-on' : ''));
    size.append(chip, el('span', 'card-size-count', `${g.cols * g.rows} 块`), pips);
    for (const tag of modLabels(m.mods)) {
      const t = el('span', `card-mod is-${tag.kind}`);
      t.dataset.mod = tag.kind;
      t.append(el('i', 'card-mod-icon'), el('span', '', tag.text));
      size.append(t);
    }

    const best = m.best;
    const bestLine = el('p', 'card-best');
    bestLine.dataset.n = String(m.n);
    if (best) {
      if (m.stars > 0) bestLine.append(starsEl(m.stars, 'card-stars'));
      bestLine.append(
        el('span', 'best-label', '最佳'),
        icon('/ui/icons/clock.png', 'best-icon'),
        el('b', 'best-time', formatTime(best.bestTimeMs)),
        el('b', 'best-moves', `${best.bestMoves}`),
        el('span', '', '步'),
      );
    } else {
      bestLine.append(el('span', 'best-none', '还没有成绩'));
    }

    const actions = el('div', 'card-actions');
    if (m.status === 'error') {
      const retry = this.button('retry', 'ui-btn--red', '内容加载失败，重试', null);
      retry.onclick = a.start;
      actions.append(retry);
    } else if (m.resume) {
      const cont = this.button('resume', 'ui-btn--yellow', `继续（${gridLabel(m.resume.grid ?? { cols: m.resume.n, rows: m.resume.n })}）`, null);
      cont.onclick = a.resume;
      const restart = this.button('restart', 'ui-btn--blue', '重新开始', '/ui/icons/reset.png');
      restart.onclick = a.start;
      actions.append(cont, restart);
    } else {
      const start = this.button('start', 'ui-btn--yellow', '开始', null);
      start.onclick = a.start;
      actions.append(start);
    }
    if (m.completed && m.status !== 'error') {
      const view = this.button('view', 'ui-btn--blue', '查看立体书', '/ui/icons/book.png');
      view.onclick = a.view;
      actions.append(view);
    }
    if (m.status === 'loading') {
      for (const b of actions.querySelectorAll('button')) b.disabled = true;
      card.classList.add('is-loading');
    }

    const cat = el('p', `card-cat${m.catFound ? ' is-found' : ''}`, m.completed ? (m.catFound ? '这里的猫：找到了 ●' : '这里的猫：还没找到 ○') : '');
    card.append(close, ...(chapter ? [chapter] : []), title, text, size, bestLine, actions);
    if (m.completed) card.append(cat);
    if (m.completed && m.runeFound !== undefined && m.runeFound !== null)
      card.append(el('p', `card-cat card-rune${m.runeFound ? ' is-found' : ''}`, m.runeFound ? '这里的符文：找到了 ●' : '这里的符文：还没找到 ○ · 查看立体书去找找'));
    this.layer.replaceChildren(card);
  }

  private button(id: string, color: string, label: string, iconSrc: string | null): HTMLButtonElement {
    const b = el('button', `ui-btn ui-btn--wide ${color}`);
    b.type = 'button';
    b.id = `card-${id}`;
    if (iconSrc) b.append(icon(iconSrc));
    b.append(el('span', '', label));
    return b;
  }
}

let pendingConfirm: ((v: boolean) => void) | null = null;

/** 返回键 / 浏览器后退：有确认框时等同点"取消"。返回是否关掉了一个确认框 */
export function cancelConfirm(): boolean {
  if (!pendingConfirm) return false;
  pendingConfirm(false);
  return true;
}

export function confirmOpen(): boolean {
  return pendingConfirm !== null;
}

/** 确认框（放弃其他地点的进度 / 重置存档） */
export function confirmDialog(message: string, yes: string, no: string): Promise<boolean> {
  const layer = document.querySelector<HTMLElement>('#confirm-layer')!;
  cancelConfirm();
  return new Promise((resolve) => {
    const box = el('section', 'ui-card ui-confirm');
    box.setAttribute('role', 'alertdialog');
    const p = el('p', 'confirm-text', message);
    const row = el('div', 'card-actions');
    const ok = el('button', 'ui-btn ui-btn--wide ui-btn--red');
    ok.type = 'button';
    ok.id = 'confirm-yes';
    ok.append(el('span', '', yes));
    const cancel = el('button', 'ui-btn ui-btn--wide ui-btn--blue');
    cancel.type = 'button';
    cancel.id = 'confirm-no';
    cancel.append(el('span', '', no));
    const done = (v: boolean) => {
      if (pendingConfirm !== done) return;
      pendingConfirm = null;
      layer.hidden = true;
      layer.replaceChildren();
      resolve(v);
    };
    ok.onclick = () => done(true);
    cancel.onclick = () => done(false);
    row.append(ok, cancel);
    box.append(p, row);
    layer.replaceChildren(box);
    layer.hidden = false;
    pendingConfirm = done;
  });
}
